import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Minus, Plus, Flame, Check, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = '/api';
const TC = '#C67A5C'; // terracotta

// Cal/min burn rates per activity
const CAL_RATES = {
  walking: 4.5, running: 10, cycling: 8, swimming: 9,
  strength: 6, yoga: 3.5, hiit: 12, elliptical: 8,
  rowing: 9, pilates: 4, dance: 7, hiking: 6.5,
  spin: 9, boxing: 10, sports: 7, other: 5,
};

// All 16 activities with terracotta SVG line icons
const ACTIVITIES = [
  { key: 'walking', label: 'Walking', icon: (c) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="4" r="2" stroke={c} strokeWidth="1.5"/><path d="M10 9h4l2 6h-2l-1 5h-2l-1-5H8l2-6z" stroke={c} strokeWidth="1.5" fill="none" strokeLinejoin="round"/></svg> },
  { key: 'running', label: 'Running', icon: (c) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="14" cy="4" r="2" stroke={c} strokeWidth="1.5"/><path d="M7 20l3-5 2 1 3-5 2 1" stroke={c} strokeWidth="1.5" strokeLinecap="round" fill="none"/><path d="M9 15l-2 5M15 11l2 3" stroke={c} strokeWidth="1.5" strokeLinecap="round" fill="none"/></svg> },
  { key: 'cycling', label: 'Cycling', icon: (c) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="7" cy="17" r="3.5" stroke={c} strokeWidth="1.5"/><circle cx="17" cy="17" r="3.5" stroke={c} strokeWidth="1.5"/><path d="M7 17l5-9h5l-3 9" stroke={c} strokeWidth="1.5" strokeLinecap="round" fill="none"/><circle cx="14" cy="6" r="1.5" stroke={c} strokeWidth="1.2"/></svg> },
  { key: 'swimming', label: 'Swimming', icon: (c) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="7" cy="7" r="2" stroke={c} strokeWidth="1.5"/><path d="M9 9l4 2 4-2" stroke={c} strokeWidth="1.5" strokeLinecap="round" fill="none"/><path d="M3 15c2-2 4-2 6 0s4 2 6 0 4-2 6 0" stroke={c} strokeWidth="1.5" fill="none"/><path d="M3 19c2-2 4-2 6 0s4 2 6 0 4-2 6 0" stroke={c} strokeWidth="1.5" fill="none"/></svg> },
  { key: 'strength', label: 'Strength', icon: (c) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><rect x="3" y="9" width="4" height="6" rx="1" stroke={c} strokeWidth="1.5"/><rect x="17" y="9" width="4" height="6" rx="1" stroke={c} strokeWidth="1.5"/><line x1="7" y1="12" x2="17" y2="12" stroke={c} strokeWidth="2"/><line x1="1" y1="12" x2="3" y2="12" stroke={c} strokeWidth="1.5" strokeLinecap="round"/><line x1="21" y1="12" x2="23" y2="12" stroke={c} strokeWidth="1.5" strokeLinecap="round"/></svg> },
  { key: 'yoga', label: 'Yoga', icon: (c) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="4" r="2" stroke={c} strokeWidth="1.5"/><path d="M12 7v5M8 19l4-7 4 7M6 12l6 2 6-2" stroke={c} strokeWidth="1.5" strokeLinecap="round" fill="none"/></svg> },
  { key: 'hiit', label: 'HIIT', icon: (c) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M5 17l3-6 3 4 3-8 3 6 2-4" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/><circle cx="12" cy="4" r="2" stroke={c} strokeWidth="1.5"/></svg> },
  { key: 'elliptical', label: 'Elliptical', icon: (c) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><ellipse cx="12" cy="16" rx="7" ry="4" stroke={c} strokeWidth="1.5" fill="none"/><path d="M12 4v8M8 8l4 4 4-4" stroke={c} strokeWidth="1.5" strokeLinecap="round" fill="none"/></svg> },
  { key: 'rowing', label: 'Rowing', icon: (c) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M4 18c4-1 8-1 16 0" stroke={c} strokeWidth="1.5" strokeLinecap="round" fill="none"/><path d="M8 14l-2 4M8 14l8-6" stroke={c} strokeWidth="1.5" strokeLinecap="round" fill="none"/><circle cx="8" cy="11" r="2" stroke={c} strokeWidth="1.5"/></svg> },
  { key: 'pilates', label: 'Pilates', icon: (c) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="5" r="2" stroke={c} strokeWidth="1.5"/><path d="M12 8v4M8 20h8M10 12l-2 8M14 12l2 8" stroke={c} strokeWidth="1.5" strokeLinecap="round" fill="none"/><path d="M6 12h12" stroke={c} strokeWidth="1.5" strokeLinecap="round"/></svg> },
  { key: 'dance', label: 'Dance', icon: (c) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="14" cy="4" r="2" stroke={c} strokeWidth="1.5"/><path d="M10 9l4-1 1 4-3 3-2 5M15 12l3 2" stroke={c} strokeWidth="1.5" strokeLinecap="round" fill="none"/><path d="M7 20l3-5" stroke={c} strokeWidth="1.5" strokeLinecap="round" fill="none"/></svg> },
  { key: 'hiking', label: 'Hiking', icon: (c) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="4" r="2" stroke={c} strokeWidth="1.5"/><path d="M10 8l2 3 2-3M10 11l-1 5-2 4M14 11l1 5 2 4" stroke={c} strokeWidth="1.5" strokeLinecap="round" fill="none"/><path d="M3 21l6-7 3 3 5-6 4 5" stroke={c} strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.4"/></svg> },
  { key: 'spin', label: 'Spin Class', icon: (c) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="14" r="6" stroke={c} strokeWidth="1.5" fill="none"/><circle cx="12" cy="14" r="2" stroke={c} strokeWidth="1.2" fill="none"/><path d="M10 6h4l1 4h-6l1-4z" stroke={c} strokeWidth="1.2" fill="none"/><circle cx="12" cy="4" r="1.5" stroke={c} strokeWidth="1.2"/></svg> },
  { key: 'boxing', label: 'Boxing', icon: (c) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M6 10c0-2 2-4 4-4h4c2 0 4 2 4 4v4c0 2-2 4-4 4h-4c-2 0-4-2-4-4v-4z" stroke={c} strokeWidth="1.5" fill="none"/><path d="M10 10v4M14 10v4" stroke={c} strokeWidth="1.5" strokeLinecap="round"/><path d="M8 6l-2-3M16 6l2-3" stroke={c} strokeWidth="1.2" strokeLinecap="round"/></svg> },
  { key: 'sports', label: 'Sports', icon: (c) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8" stroke={c} strokeWidth="1.5" fill="none"/><path d="M12 4c-2 3-2 7 0 8s2 5 0 8M4 12c3-2 7-2 8 0s5 2 8 0" stroke={c} strokeWidth="1.2" fill="none"/></svg> },
  { key: 'other', label: 'Other', icon: (c) => <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8" stroke={c} strokeWidth="1.5" fill="none"/><path d="M12 8v4l3 2" stroke={c} strokeWidth="1.5" strokeLinecap="round" fill="none"/></svg> },
];

const QUICK_DURATIONS = [15, 30, 45, 60, 90];

export function UniversalWorkoutModal({ isOpen, onClose, onLogged }) {
  const { authHeaders } = useAuth();
  const [selected, setSelected] = useState(null);
  const [duration, setDuration] = useState(30);
  const [customDuration, setCustomDuration] = useState('');
  const [calorieOverride, setCalorieOverride] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelected(null);
      setDuration(30);
      setCustomDuration('');
      setCalorieOverride('');
      setNote('');
    }
  }, [isOpen]);

  const finalDuration = customDuration ? parseInt(customDuration) || 30 : duration;
  const estCal = Math.round((CAL_RATES[selected] || 5) * finalDuration);
  const displayCal = calorieOverride ? parseInt(calorieOverride) : estCal;
  const isOverridden = calorieOverride && parseInt(calorieOverride) !== estCal;
  const selectedActivity = ACTIVITIES.find(a => a.key === selected);

  const handleLog = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.post(`${API}/exercise/log`, {
        exercise_type: selected,
        duration_minutes: finalDuration,
        calories_override: calorieOverride ? parseInt(calorieOverride) : null,
        estimated_calories: estCal,
        note: note || undefined,
      }, authHeaders());
      toast.success(`${selectedActivity?.label || 'Workout'} logged!`);
      onLogged?.();
      onClose();
    } catch {
      toast.error('Failed to log workout');
    }
    setSaving(false);
  };

  const adjustDuration = (delta) => {
    const next = Math.max(5, (customDuration ? parseInt(customDuration) || 30 : duration) + delta);
    setCustomDuration('');
    setDuration(next);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      data-testid="universal-workout-modal"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 16px' }}
    >
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)' }} />
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative', width: '100%', maxWidth: 440,
          background: '#FDFBF7', borderRadius: 24,
          boxShadow: '0 16px 48px rgba(0,0,0,0.15)',
          maxHeight: '88vh', overflowY: 'auto',
        }}
      >
        {/* Header — no title when on grid, back arrow + activity name when selected */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 10, background: '#FDFBF7',
          padding: '16px 18px 10px', borderRadius: '24px 24px 0 0',
          display: 'flex', alignItems: 'center', gap: 10,
          borderBottom: '1px solid #E8E3DB',
        }}>
          {selected ? (
            <>
              <button data-testid="workout-back-btn" onClick={() => setSelected(null)} style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex',
              }}>
                <ArrowLeft style={{ width: 20, height: 20, color: '#1A1A18' }} />
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                {selectedActivity?.icon(TC)}
                <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: '#1A1A18' }}>
                  {selectedActivity?.label}
                </span>
              </div>
            </>
          ) : (
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, letterSpacing: '0.8px', color: '#9A9590', textTransform: 'uppercase', fontWeight: 500 }}>
              Choose activity
            </span>
          )}
          <button data-testid="workout-modal-close" onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 4, marginLeft: 'auto',
          }}>
            <X style={{ width: 20, height: 20, color: '#9A9590' }} />
          </button>
        </div>

        <div style={{ padding: '14px 18px 24px' }}>
          {/* STEP 1: Activity Grid — only shown when nothing is selected */}
          {!selected && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }} data-testid="workout-activity-grid">
              {ACTIVITIES.map(act => (
                <button
                  key={act.key}
                  data-testid={`workout-type-${act.key}`}
                  onClick={() => setSelected(act.key)}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    padding: '14px 6px', borderRadius: 14, cursor: 'pointer',
                    border: 'none', background: '#FAF8F5', color: '#1A1A18',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {act.icon(TC)}
                  <span style={{ fontSize: 10, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", textAlign: 'center', lineHeight: 1.2 }}>
                    {act.label}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* STEP 2: Selected activity detail */}
          {selected && (
            <div style={{ animation: 'fadeSlideUp 0.2s ease' }}>
              {/* Duration */}
              <p style={{ fontSize: 11, letterSpacing: '0.8px', color: '#9A9590', fontFamily: "'DM Sans', sans-serif", textTransform: 'uppercase', margin: '0 0 10px', fontWeight: 500 }}>
                Duration
              </p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, marginBottom: 12 }}>
                <button data-testid="workout-duration-minus" onClick={() => adjustDuration(-5)} style={{
                  width: 44, height: 44, borderRadius: 22, border: '2px solid #E8E3DB',
                  background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Minus style={{ width: 16, height: 16, color: '#1A1A18' }} />
                </button>
                <input
                  data-testid="workout-duration-input"
                  type="number"
                  value={customDuration || duration}
                  onChange={e => setCustomDuration(e.target.value)}
                  style={{
                    width: 70, textAlign: 'center', fontSize: 28, fontWeight: 700,
                    fontFamily: "'DM Sans', sans-serif", color: '#1A1A18',
                    background: 'transparent', border: 'none', outline: 'none',
                  }}
                />
                <span style={{ fontSize: 14, color: '#9A9590', fontFamily: "'DM Sans', sans-serif", marginLeft: -16 }}>min</span>
                <button data-testid="workout-duration-plus" onClick={() => adjustDuration(5)} style={{
                  width: 44, height: 44, borderRadius: 22, border: 'none',
                  background: TC, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Plus style={{ width: 16, height: 16, color: '#fff' }} />
                </button>
              </div>
              {/* Quick pick chips */}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
                {QUICK_DURATIONS.map(d => (
                  <button
                    key={d}
                    data-testid={`workout-quick-${d}`}
                    onClick={() => { setDuration(d); setCustomDuration(''); }}
                    style={{
                      padding: '6px 16px', borderRadius: 20, border: 'none', cursor: 'pointer',
                      fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
                      background: (duration === d && !customDuration) ? `${TC}18` : '#FAF8F5',
                      color: (duration === d && !customDuration) ? TC : '#6A6560',
                      transition: 'all 0.15s',
                    }}
                  >
                    {d}m
                  </button>
                ))}
              </div>

              {/* Calories */}
              <p style={{ fontSize: 11, letterSpacing: '0.8px', color: '#9A9590', fontFamily: "'DM Sans', sans-serif", textTransform: 'uppercase', margin: '0 0 10px', fontWeight: 500 }}>
                Calories burned
              </p>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                borderRadius: 14, background: `${TC}08`, border: `1px solid ${TC}15`, marginBottom: 8,
              }}>
                <Flame style={{ width: 16, height: 16, color: TC, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: '#9A9590', fontFamily: "'DM Sans', sans-serif", flexShrink: 0 }}>
                  {isOverridden ? 'Your entry:' : `Est. ${estCal}`}
                </span>
                <input
                  data-testid="workout-calorie-input"
                  type="number"
                  min="0"
                  max="9999"
                  value={calorieOverride || estCal}
                  onChange={e => setCalorieOverride(e.target.value)}
                  onFocus={e => { if (!calorieOverride) setCalorieOverride(String(estCal)); e.target.select(); }}
                  style={{
                    width: 64, height: 32, textAlign: 'center', borderRadius: 8,
                    fontSize: 14, fontWeight: 700, fontFamily: "'DM Sans', sans-serif",
                    background: '#fff', border: `1px solid ${TC}25`, color: '#1A1A18',
                    outline: 'none', marginLeft: 'auto',
                  }}
                />
                <span style={{ fontSize: 13, fontWeight: 500, color: '#1A1A18', fontFamily: "'DM Sans', sans-serif" }}>cal</span>
              </div>
              <p style={{ fontSize: 10, color: '#9A9590', margin: '0 0 16px', fontFamily: "'DM Sans', sans-serif" }}>
                {isOverridden
                  ? `MET estimate was ${estCal} cal \u2014 using your value instead`
                  : 'Based on activity type \u00D7 duration. Edit to match your watch.'}
              </p>

              {/* Notes */}
              <p style={{ fontSize: 11, letterSpacing: '0.8px', color: '#9A9590', fontFamily: "'DM Sans', sans-serif", textTransform: 'uppercase', margin: '0 0 8px', fontWeight: 500 }}>
                Notes <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
              </p>
              <input
                data-testid="workout-note-input"
                type="text"
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="How was your workout?"
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 12,
                  fontSize: 13, fontFamily: "'DM Sans', sans-serif", color: '#1A1A18',
                  background: '#fff', border: '1px solid #E8E3DB', outline: 'none',
                  marginBottom: 20,
                }}
              />

              {/* Log Button */}
              <button
                data-testid="workout-log-submit"
                onClick={handleLog}
                disabled={saving}
                style={{
                  width: '100%', padding: '14px', borderRadius: 28, border: 'none',
                  background: TC, color: '#fff', fontSize: 15, fontWeight: 600,
                  cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                  opacity: saving ? 0.7 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition: 'opacity 0.15s',
                }}
              >
                {saving ? (
                  <div style={{ width: 18, height: 18, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                ) : (
                  <><Check style={{ width: 16, height: 16 }} /> Log {selectedActivity?.label}</>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default UniversalWorkoutModal;
